import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FiAlertTriangle,
  FiInfo,
  FiDownload,
  FiRefreshCw,
} from 'react-icons/fi';
import { MdPix } from 'react-icons/md';
import { FaBarcode } from 'react-icons/fa';
import Barcode from 'react-barcode';
import toast from 'react-hot-toast';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { contratosAPI, clienteSobreAPI } from '../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid
    ${({ $type }) => {
      switch ($type) {
        case 'error':
          return theme.colors.error[500];
        case 'success':
          return theme.colors.success[500];
        case 'info':
          return theme.colors.primary[400];
        default:
          return theme.colors.warning[500];
      }
    }};
  background: ${({ $type }) => {
    switch ($type) {
      case 'error':
        return theme.colors.error[100];
      case 'success':
        return theme.colors.success[100];
      case 'info':
        return theme.colors.primary[50];
      default:
        return theme.colors.warning[100];
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'error':
        return theme.colors.error[700];
      case 'success':
        return theme.colors.success[700];
      case 'info':
        return theme.colors.primary[700];
      default:
        return theme.colors.warning[700];
    }
  }};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const ResultadoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const ResultadoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.background.primary};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ResultadoLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
  padding: ${theme.spacing[2]} 0;
  border-bottom: 1px solid ${theme.colors.border.primary};

  &:last-child {
    border-bottom: none;
  }
`;

const ResultadoLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  text-transform: uppercase;
  font-weight: 600;
  color: ${theme.colors.text.muted};
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ResultadoValue = styled.span`
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const CopyField = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[3]};
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: ${theme.fontSizes.sm};
  word-break: break-all;
  background: ${theme.colors.background.secondary};
`;

const CopyButtonSmall = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.fontSizes.base};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[500]};
    color: ${theme.colors.background.primary};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResultadoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
`;

const PixWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[5]};
  align-items: flex-start;
  justify-content: center;
`;

const PixImage = styled.img`
  width: 180px;
  height: 180px;
  border-radius: ${theme.borderRadius.xl};
  border: 2px solid ${theme.colors.border.primary};
  background: #fff;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: ${theme.spacing[2]};
`;

const PixInfo = styled.div`
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  align-items: center;
  justify-content: center;
`;

const BarcodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  align-items: center;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.border.primary};
`;

const BarcodeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing[4]};
  background: #fff;
  border-radius: ${theme.borderRadius.lg};
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const InvoiceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  color: ${theme.colors.primary[600]};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]};
  text-align: center;
  color: ${theme.colors.text.muted};
`;

const ComprarCreditos = () => {
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState('');
  const [contrato, setContrato] = useState(null);
  const [carregandoContrato, setCarregandoContrato] = useState(false);
  const [creditosExtensao, setCreditosExtensao] = useState({
    creditos: '',
    valor_pago: '',
    erro: null,
    salvando: false,
    resultado: null,
    formaPagamento: 'cartao_credito', // 'boleto_pix' ou 'cartao_credito'
  });

  const carregarClientes = async () => {
    try {
      setLoading(true);
      setErro(null);
      // Usar a mesma API do DashboardCliente que já filtra apenas clientes acessíveis
      const response = await clienteSobreAPI.listarClientes();
      if (response.data?.success) {
        const clientesLista = response.data.data || [];
        setClientes(clientesLista);
        // Se houver apenas um cliente, selecionar automaticamente
        if (clientesLista.length === 1) {
          setClienteSelecionado(String(clientesLista[0].id));
        }
      } else {
        setErro('Não foi possível carregar os clientes.');
      }
    } catch (error) {
      console.error('Erro ao carregar clientes', error);
      setErro('Erro ao carregar clientes.');
    } finally {
      setLoading(false);
    }
  };

  const carregarContratoAtivo = async (clienteId) => {
    if (!clienteId) {
      setContrato(null);
      return;
    }

    try {
      setCarregandoContrato(true);
      const response = await contratosAPI.buscarContratoAtivoPorCliente(clienteId);
      if (response.data?.success) {
        setContrato(response.data.data);
      } else {
        setContrato(null);
      }
    } catch (error) {
      console.error('Erro ao carregar contrato ativo', error);
      setContrato(null);
    } finally {
      setCarregandoContrato(false);
    }
  };

  const resetarFormulario = () => {
    setCreditosExtensao({
      creditos: '',
      valor_pago: '',
      erro: null,
      salvando: false,
      resultado: null,
      formaPagamento: 'cartao_credito',
    });
  };

  useEffect(() => {
    carregarClientes();
  }, []);

  useEffect(() => {
    if (clienteSelecionado) {
      carregarContratoAtivo(Number.parseInt(clienteSelecionado, 10));
      // Resetar formulário quando mudar o cliente
      resetarFormulario();
    } else {
      setContrato(null);
      resetarFormulario();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteSelecionado]);

  const opcoesCreditos = useMemo(() => {
    if (!contrato || !contrato.creditos_mensais) return [];
    const creditosMensais = Number.parseInt(contrato.creditos_mensais, 10);
    if (!Number.isFinite(creditosMensais) || creditosMensais <= 0) return [];

    const opcoes = [];
    const percentuais = [0.1, 0.3, 0.5, 0.7, 1.0];

    for (const percentual of percentuais) {
      const valor = creditosMensais * percentual;
      const valorArredondado = Math.round(valor / 10) * 10;
      const valorFinal = Math.max(10, Math.min(creditosMensais, valorArredondado));
      if (!opcoes.includes(valorFinal)) {
        opcoes.push(valorFinal);
      }
    }

    return opcoes.sort((a, b) => a - b);
  }, [contrato]);

  const calcularValorExtensao = useMemo(() => {
    return (creditosSelecionados) => {
      if (!contrato || !creditosSelecionados) return '';
      const creditosInt = Number.parseInt(creditosSelecionados, 10);
      if (!Number.isFinite(creditosInt) || creditosInt <= 0) return '';

      const valorMensal = Number.parseFloat(contrato.valor_mensal);
      const creditosMensais = Number.parseInt(contrato.creditos_mensais, 10);

      if (!Number.isFinite(valorMensal) || !Number.isFinite(creditosMensais) || creditosMensais <= 0) {
        return '';
      }

      const valorComAcrescimo = valorMensal * 1.5;
      const valorPorCredito = valorComAcrescimo / creditosMensais;
      const valorTotal = valorPorCredito * creditosInt;

      const valorArredondado = Math.floor(valorTotal);
      const valorFinal = valorArredondado + 0.99;

      return valorFinal.toFixed(2);
    };
  }, [contrato]);

  useEffect(() => {
    if (contrato && opcoesCreditos.length > 0 && !creditosExtensao.creditos && !creditosExtensao.resultado) {
      const primeiroCredito = String(opcoesCreditos[0]);
      const valorCalculado = calcularValorExtensao(primeiroCredito);
      setCreditosExtensao((prev) => ({
        ...prev,
        creditos: primeiroCredito,
        valor_pago: valorCalculado,
      }));
    } else if (contrato && creditosExtensao.creditos && !creditosExtensao.resultado) {
      const valorCalculado = calcularValorExtensao(creditosExtensao.creditos);
      if (valorCalculado && valorCalculado !== creditosExtensao.valor_pago) {
        setCreditosExtensao((prev) => ({
          ...prev,
          valor_pago: valorCalculado,
        }));
      }
    }
  }, [contrato, opcoesCreditos, creditosExtensao.creditos, creditosExtensao.resultado, calcularValorExtensao]);

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getCobrancaStatusLabel = (status) => {
    const normalized = (status || '').toUpperCase();
    switch (normalized) {
      case 'PENDING':
        return 'Pendente';
      case 'RECEIVED':
      case 'CONFIRMED':
        return 'Pago';
      case 'OVERDUE':
        return 'Atrasado';
      default:
        return status || 'Desconhecido';
    }
  };

  const getPixImageSrc = (pixQr) => {
    if (!pixQr) return null;

    const encodedImage =
      typeof pixQr === 'string'
        ? pixQr
        : typeof pixQr.encodedImage === 'string'
        ? pixQr.encodedImage
        : null;

    if (encodedImage && encodedImage.startsWith('data:')) {
      return encodedImage;
    }

    if (encodedImage) {
      return `data:image/png;base64,${encodedImage}`;
    }

    const qrCodeImage =
      typeof pixQr === 'string'
        ? pixQr
        : typeof pixQr.qrCodeImage === 'string'
        ? pixQr.qrCodeImage
        : null;

    return qrCodeImage;
  };

  const extrairPayloadPix = (pixQrCode) => {
    if (!pixQrCode) return null;
    
    if (typeof pixQrCode === 'string') {
      return pixQrCode;
    }
    
    if (typeof pixQrCode === 'object') {
      return pixQrCode.payload || pixQrCode.qrCode || null;
    }
    
    return null;
  };

  const copiarConteudo = (texto, label) => {
    if (!texto) {
      toast.error(`${label} não disponível`);
      return;
    }
    navigator.clipboard.writeText(texto).then(
      () => {
        toast.success(`${label} copiado!`);
      },
      () => {
        toast.error(`Erro ao copiar ${label}`);
      }
    );
  };

  const adicionarExtensao = async () => {
    if (!contrato || !contrato.id) return;
    const creditosInt = Number.parseInt(creditosExtensao.creditos, 10);
    const valorFloat = Number.parseFloat(creditosExtensao.valor_pago);
    if (Number.isNaN(creditosInt) || creditosInt <= 0 || Number.isNaN(valorFloat) || valorFloat < 0) {
      setCreditosExtensao((prev) => ({
        ...prev,
        erro: 'Informe créditos (>0) e valor válido.',
      }));
      return;
    }

    try {
      setCreditosExtensao((prev) => ({ ...prev, salvando: true, erro: null }));
      // Não passar cicloId - a API buscará o ciclo atual automaticamente
      const payload = {
        creditos: creditosInt,
        forma_pagamento: creditosExtensao.formaPagamento,
      };
      const response = await contratosAPI.solicitarExtensao(contrato.id, payload);
      if (response.data?.success) {
        toast.success('Cobrança criada com sucesso. Acompanhe os dados abaixo.');
        setCreditosExtensao((prev) => ({
          ...prev,
          resultado: response.data.data,
          salvando: false,
        }));
        await carregarContratoAtivo(Number.parseInt(clienteSelecionado, 10));
      } else {
        throw new Error(response.data?.error || 'Não foi possível criar a extensão.');
      }
    } catch (error) {
      console.error('Erro ao adicionar extensão', error);
      setCreditosExtensao((prev) => ({
        ...prev,
        salvando: false,
        erro: error.response?.data?.error || 'Erro ao adicionar extensão.',
      }));
    }
  };

  const linkPagamentoExtensao = creditosExtensao.resultado
    ? creditosExtensao.resultado.bankSlipUrl || creditosExtensao.resultado.invoiceUrl || null
    : null;

  if (loading) {
    return (
      <Layout title="Comprar Créditos">
        <Container>
          <Card>
            <Subtitle>Carregando clientes...</Subtitle>
          </Card>
        </Container>
      </Layout>
    );
  }

  const mostrarFiltroCliente = clientes.length > 1;
  const mostrarBotaoAtualizar = mostrarFiltroCliente || clienteSelecionado;
  const mostrarHeader = mostrarFiltroCliente;

  return (
    <Layout title="Comprar Créditos">
      <Container>
        {mostrarHeader && (
          <Header>
            <HeaderGroup>
              <Title>Comprar Créditos</Title>
              <Subtitle>Adicione créditos ao ciclo atual do cliente</Subtitle>
            </HeaderGroup>
            {mostrarBotaoAtualizar && (
              <ActionBar>
                <PrimaryButton onClick={carregarClientes}>
                  <FiRefreshCw />
                  Atualizar
                </PrimaryButton>
              </ActionBar>
            )}
          </Header>
        )}

        <Card>
          {mostrarFiltroCliente && (
            <FormGroup>
              <Label>Cliente *</Label>
              <Select
                value={clienteSelecionado}
                onChange={(e) => setClienteSelecionado(e.target.value)}
              >
                <option value="">Selecione um cliente</option>
                {clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome_fantasia || cliente.nome_social || `Cliente #${cliente.id}`}
                  </option>
                ))}
              </Select>
            </FormGroup>
          )}

          {carregandoContrato && (
            <Alert $type="info">
              <FiInfo />
              Carregando informações do contrato...
            </Alert>
          )}

          {!carregandoContrato && clienteSelecionado && !contrato && (
            <Alert $type="warning">
              <FiAlertTriangle />
              Nenhum contrato ativo encontrado para este cliente.
            </Alert>
          )}

          {!carregandoContrato && contrato && (
            <>
              <FormGroup>
                <Label>Contrato</Label>
                <Input
                  type="text"
                  value={`Contrato #${contrato.id} - ${contrato.nome_plano || 'Sem plano'}`}
                  readOnly
                  style={{ cursor: 'not-allowed', opacity: 0.8 }}
                />
              </FormGroup>

              {contrato.ciclo_atual ? (
                <FormGroup>
                  <Label>Ciclo Atual</Label>
                  <Input
                    type="text"
                    value={`${formatDate(contrato.ciclo_atual.inicio)} - ${formatDate(contrato.ciclo_atual.fim)}`}
                    readOnly
                    style={{ cursor: 'not-allowed', opacity: 0.8 }}
                  />
                </FormGroup>
              ) : (
                <Alert $type="warning">
                  <FiAlertTriangle />
                  Nenhum ciclo ativo encontrado para este contrato.
                </Alert>
              )}

              {contrato.ciclo_atual && !contrato.ciclo_atual.encerrado && (
                <>
                  {creditosExtensao.resultado ? (
                    <ResultadoWrapper>
                      <ResultadoBox>
                        <ResultadoLine>
                          <ResultadoLabel>Contrato</ResultadoLabel>
                          <ResultadoValue>Contrato #{contrato.id}</ResultadoValue>
                        </ResultadoLine>
                        <ResultadoLine>
                          <ResultadoLabel>Ciclo</ResultadoLabel>
                          <ResultadoValue>
                            {formatDate(contrato.ciclo_atual.inicio)} - {formatDate(contrato.ciclo_atual.fim)}
                          </ResultadoValue>
                        </ResultadoLine>
                        <ResultadoLine>
                          <ResultadoLabel>Status</ResultadoLabel>
                          <ResultadoValue>
                            {getCobrancaStatusLabel(creditosExtensao.resultado.status)}
                          </ResultadoValue>
                        </ResultadoLine>
                        <ResultadoLine>
                          <ResultadoLabel>Valor da cobrança</ResultadoLabel>
                          <ResultadoValue>{formatCurrency(creditosExtensao.resultado.valor)}</ResultadoValue>
                        </ResultadoLine>
                      </ResultadoBox>

                      {creditosExtensao.formaPagamento === 'boleto_pix' && (
                        <>
                          <ResultadoBox>
                            <ResultadoHeader>
                              <ResultadoLabel>
                                <FaBarcode style={{ marginRight: theme.spacing[2] }} />
                                Linha digitável
                              </ResultadoLabel>
                              {creditosExtensao.resultado.linhaDigitavel && (
                                <CopyButtonSmall
                                  type="button"
                                  onClick={() =>
                                    copiarConteudo(creditosExtensao.resultado.linhaDigitavel, 'Linha digitável')
                                  }
                                  title="Copiar linha digitável"
                                >
                                  <FaBarcode />
                                </CopyButtonSmall>
                              )}
                            </ResultadoHeader>
                            {creditosExtensao.resultado.linhaDigitavel ? (
                              <BarcodeWrapper>
                                <BarcodeContainer>
                                  <Barcode
                                    value={creditosExtensao.resultado.linhaDigitavel.replace(/\s/g, '')}
                                    format="CODE128"
                                    width={2}
                                    height={70}
                                    displayValue={false}
                                  />
                                </BarcodeContainer>
                              </BarcodeWrapper>
                            ) : (
                              <span style={{ color: theme.colors.text.muted }}>
                                Aguardando geração da linha digitável...
                              </span>
                            )}
                          </ResultadoBox>

                          <ResultadoBox>
                            <ResultadoHeader>
                              <ResultadoLabel>
                                <MdPix style={{ marginRight: theme.spacing[2] }} />
                                Pix
                              </ResultadoLabel>
                              {(extrairPayloadPix(creditosExtensao.resultado.pixQrCode) || creditosExtensao.resultado.pixPayload) && (
                                <CopyButtonSmall
                                  type="button"
                                  onClick={() =>
                                    copiarConteudo(extrairPayloadPix(creditosExtensao.resultado.pixQrCode) || creditosExtensao.resultado.pixPayload, 'Código Pix')
                                  }
                                  title="Copiar código Pix"
                                >
                                  <MdPix />
                                </CopyButtonSmall>
                              )}
                            </ResultadoHeader>
                            <PixWrapper>
                              {getPixImageSrc(creditosExtensao.resultado.pixQrCode) ? (
                                <PixImage
                                  src={getPixImageSrc(creditosExtensao.resultado.pixQrCode)}
                                  alt="QRCode Pix"
                                />
                              ) : (
                                <span style={{ color: theme.colors.text.muted }}>
                                  QR Code disponível diretamente na invoice.
                                </span>
                              )}
                            </PixWrapper>
                          </ResultadoBox>

                          {linkPagamentoExtensao && (
                            <div style={{ marginTop: theme.spacing[2] }}>
                              <InvoiceLink href={linkPagamentoExtensao} target="_blank" rel="noreferrer" title="Baixar PDF">
                                <FiDownload />
                                Baixar PDF
                              </InvoiceLink>
                            </div>
                          )}

                          <Alert $type="info">
                            <FiInfo />
                            Após a confirmação do pagamento pelo Asaas, os créditos serão aplicados
                            automaticamente ao ciclo atual.
                          </Alert>

                          <ActionBar>
                            <GhostButton onClick={resetarFormulario}>Nova cobrança</GhostButton>
                            <PrimaryButton
                              onClick={() =>
                                linkPagamentoExtensao && window.open(linkPagamentoExtensao, '_blank')
                              }
                              disabled={!linkPagamentoExtensao}
                            >
                              Abrir fatura
                            </PrimaryButton>
                          </ActionBar>
                        </>
                      )}

                      {creditosExtensao.formaPagamento === 'cartao_credito' && (
                        <>
                          <Alert $type="success">
                            <FiInfo />
                            Pagamento via cartão de crédito processado. Os créditos foram aplicados automaticamente ao ciclo atual.
                          </Alert>
                          <ActionBar>
                            <PrimaryButton onClick={resetarFormulario}>Nova cobrança</PrimaryButton>
                          </ActionBar>
                        </>
                      )}
                    </ResultadoWrapper>
                  ) : (
                    <>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: theme.spacing[4],
                        padding: theme.spacing[4],
                        background: theme.colors.background.primary,
                        borderRadius: theme.borderRadius.xl,
                        border: `1px solid ${theme.colors.border.primary}`
                      }}>
                        <FormGroup>
                          <Label>Créditos adicionais *</Label>
                          <Select
                            value={creditosExtensao.creditos}
                            onChange={(e) => setCreditosExtensao((prev) => ({ ...prev, creditos: e.target.value }))}
                          >
                            <option value="">Selecione a quantidade</option>
                            {opcoesCreditos.map((valor) => (
                              <option key={valor} value={valor}>
                                {valor} créditos
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                        <FormGroup>
                          <Label>Valor estimado</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={creditosExtensao.valor_pago}
                            readOnly
                            style={{ cursor: 'not-allowed', opacity: 0.8 }}
                            title="Valor calculado automaticamente com base nos créditos selecionados"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Forma de pagamento *</Label>
                          <Select
                            value={creditosExtensao.formaPagamento}
                            onChange={(e) => setCreditosExtensao((prev) => ({ ...prev, formaPagamento: e.target.value }))}
                          >
                            <option value="cartao_credito">Cartão de Crédito</option>
                            <option value="boleto_pix">Boleto/Pix</option>
                          </Select>
                        </FormGroup>
                        {creditosExtensao.formaPagamento === 'boleto_pix' && (
                          <Alert $type="info">
                            <FiInfo />
                            Caso pague no boleto, pode demorar até 3 dias para compensar o pagamento. Os créditos serão contabilizados após a compensação do boleto.
                          </Alert>
                        )}
                        {creditosExtensao.erro && (
                          <Alert $type="error">
                            <FiAlertTriangle />
                            {creditosExtensao.erro}
                          </Alert>
                        )}
                        <ActionBar>
                          <PrimaryButton onClick={adicionarExtensao} disabled={creditosExtensao.salvando}>
                            {creditosExtensao.salvando ? 'Gerando...' : 'Gerar cobrança'}
                          </PrimaryButton>
                        </ActionBar>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {!clienteSelecionado && !mostrarFiltroCliente && (
            <EmptyState>
              <p>Nenhum cliente disponível.</p>
            </EmptyState>
          )}
        </Card>
      </Container>

    </Layout>
  );
};

export default ComprarCreditos;

